import {
  COUNTRY,
  CountryEnv,
  DefaultCountryConfig,
  LANGUAGE,
  Maybe
} from "@type/index";
import { convertRegionName } from "@util/country";
import { SB_ENV } from "@config/common";
import store from "../../redux/store";

const MILLISECONDS = 1000;
const SEC = 60;
const MINUTE = SEC * MILLISECONDS;

const defaultConfig: DefaultCountryConfig = {
  countryRegion: "Asia/Tokyo",
  timeZone: {},
  timeZoneParentCategory: {
    EST: "ET",
    EDT: "ET"
  },
  dateFormat: {}
};

const getTimeZone = (_language: LANGUAGE, countryConfig: CountryEnv) =>
  countryConfig.timeZone?.[_language] ?? {
    defaultTimezone: "",
    offset: {}
  };

const getDateFormat = (_language: LANGUAGE, countryConfig: CountryEnv) =>
  countryConfig.dateFormat?.[_language] || "";

const getCountryRegion = (countryConfig: CountryEnv) =>
  countryConfig.countryRegion ?? defaultConfig.countryRegion;

const getTimeZoneParentCategory = (type: string, countryConfig: CountryEnv) =>
  countryConfig.timeZoneParentCategory?.[type] ??
  defaultConfig.timeZoneParentCategory?.[type];

function convertToDatetime(date?: number): number {
  if (date) {
    // Convert from seconds to milliseconds.
    // If the timestamp is 10 digits, it needs to be multiplied by 1000. If the timestamp is 13 digits, it does not need
    return date.toString().length === 10 ? date * MILLISECONDS : date;
  }
  return 0;
}

const formatDate = (
  value: string | number,
  _language: LANGUAGE,
  countryConfig: CountryEnv
): string => {
  const updatedVal =
    typeof value === "number" ? convertToDatetime(value) : value;
  const dateObj = new Date(updatedVal);
  // takes date format from country configuration
  const newDate = getDateFormat(_language, countryConfig);
  const date = dateObj.getDate();
  const month = dateObj.getMonth() + 1;
  const year = dateObj.getFullYear();

  const formattedDate = newDate
    .replace("<dd>", `0${date}`.slice(-2))
    .replace("<DD>", `${date}`)
    .replace("<mm>", `0${month}`.slice(-2))
    .replace("<MM>", `${month}`)
    .replace("<yyyy>", `${year}`);
  return formattedDate;
};

class Moment {
  date: Date;

  constructor(date?: number | Date | null) {
    this.date = typeof date !== "undefined" ? new Date(date || 0) : new Date();
  }

  toTimeForTimeZone(
    _country: COUNTRY,
    _language: LANGUAGE,
    countryConfig: CountryEnv,
    timeZoneLanguage?: LANGUAGE
  ): {
    date: number;
    timeZoneName: string;
  } {
    const timestamp: number = this.date.getTime();
    let intlDate: Maybe<Array<string>>;
    //pass timeZoneLanguage to Intl to show the expected time zone for different countires,use the i18n language for the country config
    const languagePassedToIntl = timeZoneLanguage ?? _language;
    try {
      intlDate =
        window.Intl &&
        window.Intl.DateTimeFormat(
          `${languagePassedToIntl.toLowerCase()}-${_country.toUpperCase()}`,
          {
            timeZone: getCountryRegion(countryConfig),
            timeZoneName: "short"
          }
        )
          .format(timestamp)
          .split(" ");
    } catch (error) {
      // IE11 does not support IANA time zone names (e.g. `ASIA/TOKYO`) - use `getTimeZone` fallback
    }

    const timeZoneName: string =
      intlDate && intlDate.length
        ? intlDate[intlDate.length - 1]
        : getTimeZone(_language, countryConfig).defaultTimezone;
    const utc = timestamp + this.date.getTimezoneOffset() * MINUTE;

    // Add offset (-5/-4) to UTC time to convert to EST/EDT
    const timeZone =
      getTimeZone(_language, countryConfig).offset[timeZoneName] || 0;
    const dateWithOffset = utc + SEC * MINUTE * timeZone;
    // timeZoneParentCategory hasn't been used yet
    const timeZoneValidatedName =
      getTimeZoneParentCategory(timeZoneName, countryConfig) ?? timeZoneName;
    return { date: dateWithOffset, timeZoneName: timeZoneValidatedName };
  }
}

function moment(
  date?: number | Date | Moment | null,
  useUnixTime?: boolean
): Moment {
  if (typeof date === "number") {
    return useUnixTime ? new Moment(date) : new Moment(convertToDatetime(date));
  }
  if (typeof date === "object") {
    if (date && date instanceof Moment) {
      return date;
    }
    return new Moment(date);
  }
  return new Moment();
}

export const formatDateTimeWithTimezone = (
  value: number | Date,
  timeZoneLanguage?: LANGUAGE
): string => {
  /* new country configuration */
  const { region, language, brand_name } = store.getState().config;
  const countryConfig: CountryEnv = SB_ENV(brand_name, region);
  const _country = convertRegionName(region) as COUNTRY;
  const _language = language as LANGUAGE;
  // timeZoneName hasn't been used yet
  const { date } = moment(value).toTimeForTimeZone(
    _country,
    _language,
    countryConfig,
    timeZoneLanguage
  );
  return formatDate(date, _language, countryConfig);
};
